import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLoading, useAuth } from '../check_login';
import TrumbowygEditor from '../../common/TrumbowygEditor';
import PlaceholderDropdown from '../../common/PlaceholderDropdown';
import TagsInput from '../../common/TagsInput';
import { FiSave, FiChevronLeft, FiGlobe, FiClock } from 'react-icons/fi';
import { TbPlus, TbCalendarEvent, TbX } from 'react-icons/tb';
import { motion } from 'framer-motion';
import { format, addDays } from 'date-fns';
import VersionHistory from '../../pages/view/VersionHistory';
import '../../../assets/css/editForm.css'

// SMS Character Counter Function
const calculateSMSStats = (text) => {
  if (!text) return { totalCount: 0, totalSMS: 0, gsm: 0, unicode: 0, nextPart: 160 };
  
  const textLength = text.length;
  const gsmChars = /^[A-Za-z0-9@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[\]\|~€]*$/;
  const isUnicode = !gsmChars.test(text);
  
  let smsLimit = isUnicode ? 70 : 160;
  let totalSMS = Math.ceil(textLength / smsLimit);
  let nextPart = smsLimit - (textLength % smsLimit);
  
  if (nextPart === smsLimit) nextPart = 0;
  
  return {
    totalCount: textLength,
    totalSMS: totalSMS,
    gsm: isUnicode ? 0 : textLength,
    unicode: isUnicode ? textLength : 0,
    nextPart: nextPart
  };
};

const EditCreative = () => {
  const { showLoading, hideLoading } = useLoading();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { lang, id } = useParams();
  const [searchParams] = useSearchParams();
  const [creativeData, setCreativeData] = useState({});
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(lang);
  const [error, setError] = useState('');
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [creative, setCreative] = useState('Email');
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  const [selectedLanguagesToTranslate, setSelectedLanguagesToTranslate] = useState([]);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [currentTranslatingLanguage, setCurrentTranslatingLanguage] = useState('');
  const [totalLanguages, setTotalLanguages] = useState(0);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [highlightVersionId, setHighlightVersionId] = useState(null);
  const [showAddLanguageModal, setShowAddLanguageModal] = useState(false);
  const [selectedLanguagesToAdd, setSelectedLanguagesToAdd] = useState([]);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Status states
  const [statuses, setStatuses] = useState([]);
  const [statusSystemEnabled, setStatusSystemEnabled] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('');
  const [statusLoaded, setStatusLoaded] = useState(false);

  // Permission check
  const permissions = currentUser?.role?.permissions || {};
  const canViewHistory = permissions.canViewHistory || currentUser?.isAdmin;
  const canAITranslateSMS = permissions.canAITranslateSMS || currentUser?.isAdmin;
  const canAITranslateEmail = permissions.canAITranslateEmail || currentUser?.isAdmin;
  const canAITranslatePopup = permissions.canAITranslatePopup || currentUser?.isAdmin;
  const canAITranslatePushNotification = permissions.canAITranslatePushNotification || currentUser?.isAdmin;
  const canManageCampaigns = permissions.canManageCampaigns || currentUser?.isAdmin;

  // Campaign assignment states
  const [campaigns, setCampaigns] = useState([]);
  const [campaignAssignments, setCampaignAssignments] = useState([]);
  const [selectedCampaignForAssign, setSelectedCampaignForAssign] = useState('');
  const [selectedDateForAssign, setSelectedDateForAssign] = useState('');

  // Check if user can AI translate the current creative type based on creative state
  const canAITranslateCurrentType = 
    (creative?.toLowerCase() === 'sms' && canAITranslateSMS) ||
    (creative?.toLowerCase() === 'email' && canAITranslateEmail) ||
    (creative?.toLowerCase() === 'popup' && canAITranslatePopup) ||
    (creative?.toLowerCase() === 'push notification' && canAITranslatePushNotification);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  // Check URL parameters for openHistory
  useEffect(() => {
    const openHistory = searchParams.get('openHistory');
    const versionId = searchParams.get('versionId');
    if (openHistory === 'true') {
      setShowVersionHistory(true);
      // Store versionId in state if present
      if (versionId) {
        setHighlightVersionId(versionId);
      }
      // Remove the parameters from URL
      searchParams.delete('openHistory');
      searchParams.delete('versionId');
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    const fetchCreativeData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          setError('You are not logged in');
          setIsLoading(false);
          return;
        }

        // If the language already exists in creativeData (manually added), skip loading
        if (creativeData[selectedLanguage]) {
          setIsLoading(false);
          return;
        }

        const response = await axios.get(`/view/${selectedLanguage}/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const availableLanguages = response.data.availableLanguages || [];

        // Preserve manually added languages that might not be in server response yet
        const manuallyAddedLangs = languages.filter(lang => !availableLanguages.includes(lang));
        setLanguages([...availableLanguages, ...manuallyAddedLangs]);

        const currentContent = response.data.content || {};
        setContent(currentContent);
        setCreative(response.data.creative || 'Email');
        
        // Set current status only on first load
        if (response.data.status && !statusLoaded) {
          setCurrentStatus(response.data.status._id || response.data.status);
          setStatusLoaded(true);
        }

        // Only update if the data for this language doesn't exist yet
        // This preserves any edits made while switching languages
        setCreativeData((prevState) => {
          const newState = {
            ...prevState,
            title: response.data.title,
            tags: response.data.tags || [],
          };
          
          // Only set content for the selected language if it doesn't already exist in state
          // This preserves any edits when switching languages
          if (!prevState[selectedLanguage]) {
            newState[selectedLanguage] = currentContent;
          }
          
          return newState;
        });

        // No need to initialize stats here; they derive from current content

        // Fetch all available languages for translation (only on first load)
        if (availableLanguages.length > 0) {
          const languagesResponse = await axios.get('/language/view', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const activeLanguages = languagesResponse.data.filter(lang => lang.status === 'active');
          setAvailableLanguages(activeLanguages);
        }

      } catch (err) {
        console.error('Error fetching creative data:', err);
        setError('Error fetching creative data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCreativeData();
  }, [id, selectedLanguage]); // Премахнах creativeData от dependencies за да избегнем безкраен цикъл

  // Fetch status data on mount
  useEffect(() => {
    const fetchStatusData = async () => {
      try {
        const token = localStorage.getItem('token');
        const [statusesRes, settingsRes] = await Promise.all([
          axios.get('/api/statuses/list', { headers: { Authorization: `Bearer ${token}` }}),
          axios.get('/api/statuses/settings', { headers: { Authorization: `Bearer ${token}` }})
        ]);
        setStatuses(statusesRes.data);
        setStatusSystemEnabled(settingsRes.data.isEnabled);
      } catch (err) {
        console.error('Error fetching status data:', err);
      }
    };
    fetchStatusData();
  }, []);

  // Fetch campaigns + this creative's existing assignments
  const fetchCampaignAssignments = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/campaigns/for-creative/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampaignAssignments(response.data);
    } catch (err) {
      console.error('Error fetching campaign assignments:', err);
    }
  }, [id]);

  useEffect(() => {
    if (!canManageCampaigns) return;

    const fetchCampaigns = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/campaigns/list', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCampaigns(response.data);
      } catch (err) {
        console.error('Error fetching campaigns:', err);
      }
    };

    fetchCampaigns();
    fetchCampaignAssignments();
  }, [canManageCampaigns, fetchCampaignAssignments]);

  const toDateKey = (date) => format(date, 'yyyy-MM-dd');

  const getAssignableDays = (campaign) => {
    if (!campaign) return [];
    const todayKey = toDateKey(new Date());
    const result = [];
    let cursor = new Date(campaign.startDate);
    const end = new Date(campaign.endDate);
    while (toDateKey(cursor) <= toDateKey(end)) {
      if (toDateKey(cursor) > todayKey) {
        result.push(new Date(cursor));
      }
      cursor = addDays(cursor, 1);
    }
    return result;
  };

  const selectedCampaignForAssignObj = campaigns.find(
    (c) => String(c._id) === String(selectedCampaignForAssign)
  );
  const assignableDays = getAssignableDays(selectedCampaignForAssignObj);

  const handleAssignToCampaign = async () => {
    if (!selectedCampaignForAssign || !selectedDateForAssign) {
      showNotification('Please select a campaign and a date', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/campaigns/${selectedCampaignForAssign}/days/${selectedDateForAssign}`,
        { creativeId: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification('Creative assigned to campaign', 'success');
      setSelectedDateForAssign('');
      fetchCampaignAssignments();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to assign creative', 'error');
    }
  };

  const handleRemoveAssignment = async (campaignId, date) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/campaigns/${campaignId}/days/${date}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showNotification('Removed from campaign', 'success');
      fetchCampaignAssignments();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to remove assignment', 'error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCreativeData((prevState) => ({
      ...prevState,
      [selectedLanguage]: {
        ...prevState[selectedLanguage],
        [name]: value,
      },
      [name]: value,
    }));
  };

  const handleEditorChange = useMemo(() => (content) => {
    setCreativeData((prevState) => ({
      ...prevState,
      [selectedLanguage]: {
        ...prevState[selectedLanguage],
        body: content,
      },
    }));
  }, [selectedLanguage]);

  const handleAdditionalTextChange = useMemo(() => (content) => {
    setCreativeData((prevState) => ({
      ...prevState,
      [selectedLanguage]: {
        ...prevState[selectedLanguage],
        additionalText: content,
      },
    }));
  }, [selectedLanguage]);

  const handleAdditionalTextUnderBTNChange = useMemo(() => (content) => {
    setCreativeData((prevState) => ({
      ...prevState,
      [selectedLanguage]: {
        ...prevState[selectedLanguage],
        additionalTextUnderBTN: content,
      },
    }));
  }, [selectedLanguage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    showLoading();

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You are not logged in');
        return;
      }

      // Check if we need to create Original version BEFORE updating
      try {
        const versionsResponse = await axios.get(`/creative/versions/${id}/versions`);
        const versions = versionsResponse.data;
        
        if (versions.length === 0) {
          // No versions exist - create Original version BEFORE updating
          const user = localStorage.getItem('user') || 'Admin';
          await axios.post(`/creative/versions/${id}/create-version`, {
            editedBy: user,
            changesSummary: 'Original version'
          });
        }
      } catch (versionError) {
        console.error('Error checking/creating original version:', versionError);
      }

      const updatedData = { ...creativeData };
      
      // Include status if system is enabled
      if (statusSystemEnabled && currentStatus) {
        updatedData.status = currentStatus;
      }
      
      const response = await axios.put(`/creative/${id}`, updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status !== 200) {
        throw new Error('Failed to update creative');
      }

      // Create new version after successful update
      try {
        const user = localStorage.getItem('user') || 'Admin';
        await axios.post(`/creative/versions/${id}/create-version`, {
          editedBy: user,
          changesSummary: 'Updated creative content'
        });
      } catch (versionError) {
        console.error('Error creating version:', versionError);
        // Don't fail the update if version creation fails
      }

      navigate('/library/creatives');
    } catch (err) {
      console.error('Error updating creative:', err);
      setError('Error updating creative');
    } finally {
      hideLoading();
    }
  };

  // Get short code for a language name, falling back to the name itself
  const getLangCode = (name) => {
    const match = availableLanguages.find(l => l.languageName === name);
    return match?.code ? match.code.toUpperCase() : name.toUpperCase();
  };

  // Get languages that haven't been translated yet
  const getUntranslatedLanguages = () => {
    return availableLanguages.filter(availableLang => 
      !languages.includes(availableLang.languageName)
    );
  };

  const handleTranslateToLanguages = async () => {
    if (selectedLanguagesToTranslate.length === 0) {
      showNotification('Please select at least one language to translate to.', 'error');
      return;
    }

    setIsTranslating(true);
    setTranslationProgress(0);
    setTotalLanguages(selectedLanguagesToTranslate.length);
    setCurrentTranslatingLanguage('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Get the current content from the selected language as source
      const sourceContent = creativeData[selectedLanguage];

      const translateData = {
        languages: selectedLanguagesToTranslate,
        ...sourceContent
      };

      let endpoint;
      switch (creative.toLowerCase()) {
        case 'email':
          endpoint = `/translate/email/add-translations/${id}`;
          break;
        case 'popup':
          endpoint = `/translate/popup/add-translations/${id}`;
          break;
        case 'sms':
          endpoint = `/translate/sms/add-translations/${id}`;
          break;
        case 'push-notification':
          endpoint = `/translate/push-notification/add-translations/${id}`;
          break;
        default:
          throw new Error('Unsupported creative type for translation');
      }

      // Симулиране на progress докато се извършва превода
      const selectedLanguageNames = selectedLanguagesToTranslate.map(langId => {
        const lang = availableLanguages.find(l => l._id === langId);
        return lang ? lang.languageName : 'Unknown';
      });

      // Progress simulation
      const progressInterval = setInterval(() => {
        setTranslationProgress(prev => {
          if (prev < 90) {
            const increment = Math.random() * 15 + 5; // Random increment between 5-20%
            const newProgress = Math.min(prev + increment, 90);
            
            // Update current language being translated
            const currentLangIndex = Math.floor((newProgress / 90) * selectedLanguageNames.length);
            if (currentLangIndex < selectedLanguageNames.length) {
              setCurrentTranslatingLanguage(selectedLanguageNames[currentLangIndex]);
            }
            
            return newProgress;
          }
          return prev;
        });
      }, 800);

      const response = await axios.post(endpoint, translateData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      clearInterval(progressInterval);
      setTranslationProgress(100);
      setCurrentTranslatingLanguage('Finalizing...');

      // Wait a moment to show 100%
      await new Promise(resolve => setTimeout(resolve, 500));

      if (response.status === 200) {
        // Refresh the page to include new translations
        window.location.reload();
      }
    } catch (error) {
      console.error('Translation error:', error);
      if (error.response && error.response.data && error.response.data.error) {
        setError(`Translation failed: ${error.response.data.error}`);
      } else {
        setError('Failed to translate to selected languages');
      }
    } finally {
      setIsTranslating(false);
      setShowTranslateModal(false);
      setTranslationProgress(0);
      setCurrentTranslatingLanguage('');
    }
  };

  const toggleLanguageForTranslation = (languageId) => {
    setSelectedLanguagesToTranslate(prev =>
      prev.includes(languageId)
        ? prev.filter(id => id !== languageId)
        : [...prev, languageId]
    );
  };

  const handleAddLanguageManually = async () => {
    if (selectedLanguagesToAdd.length === 0) {
      showNotification('Please select at least one language to add.', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Check if version exists, create Original if not
      try {
        const versionsResponse = await axios.get(`/creative/versions/${id}/versions`);
        const versions = versionsResponse.data;
        
        if (versions.length === 0) {
          const user = localStorage.getItem('user') || 'Admin';
          await axios.post(`/creative/versions/${id}/create-version`, {
            editedBy: user,
            changesSummary: 'Original version'
          });
        }
      } catch (versionError) {
        console.error('Error checking/creating original version:', versionError);
      }

      // Add empty language entries for all selected languages
      const newLanguagesData = {};
      const newLanguageNames = [];

      selectedLanguagesToAdd.forEach(langId => {
        const selectedLang = availableLanguages.find(l => l._id === langId);
        if (selectedLang) {
          newLanguagesData[selectedLang.languageName] = {
            body: '',
            headline: '',
            bodyBtnName: '',
            bodyBtnLink: '',
            subjectLine1: '',
            subjectLine2: '',
            subjectLine3: '',
            preHeader: '',
            additionalText: '',
            additionalTextUnderBTN: ''
          };
          newLanguageNames.push(selectedLang.languageName);
        }
      });

      setCreativeData(prev => ({
        ...prev,
        ...newLanguagesData
      }));

      setLanguages(prev => [...prev, ...newLanguageNames]);
      setSelectedLanguage(newLanguageNames[0]); // Switch to first added language
      setShowAddLanguageModal(false);
      setSelectedLanguagesToAdd([]);
      
      showNotification(`${newLanguageNames.length} language(s) added: ${newLanguageNames.join(', ')}. Please fill in the content and save.`, 'success');
    } catch (error) {
      console.error('Error adding languages:', error);
      showNotification('Failed to add languages', 'error');
    }
  };

  const toggleLanguageToAdd = (languageId) => {
    setSelectedLanguagesToAdd(prev =>
      prev.includes(languageId)
        ? prev.filter(id => id !== languageId)
        : [...prev, languageId]
    );
  };

  // Мемоизираме editor конфигурацията
  const editorConfig = useMemo(() => ({
    height: 500,
    menubar: false,
    plugins: [
      'advlist autolink lists link image charmap print preview anchor',
      'searchreplace visualblocks code fullscreen',
      'insertdatetime media table paste code help wordcount',
    ],
    toolbar: 'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | ltr rtl | removeformat | code | help',
    directionality: 'ltr',
    content_style: document.body.classList.contains('dark-mode') 
      ? 'body { background: #2c2c3c !important; color: #ffffff !important; font-family:Helvetica,Arial,sans-serif; font-size:14px; direction: ltr; } * { color: #ffffff !important; }'
      : 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px; direction: ltr; }',
    // Премахваме setup функцията която може да причинява проблеми
    // Предотвратяваме автоматично фокусиране при init
    auto_focus: false,
    // Подобряваме paste поведението
    paste_as_text: false,
    paste_auto_cleanup_on_paste: true,
    paste_remove_styles_if_webkit: true,
    // Подобряваме undo/redo функционалността
    custom_undo_redo_levels: 50,
    // Стабилизираме селекцията
    object_resizing: false,
    resize: false,
    // Подобряваме keyboard navigation
    forced_root_block: 'p',
    force_br_newlines: false,
    force_p_newlines: false,
    remove_script_host: false,
    // Подобрение за cursor positioning
    keep_styles: false,
    verify_html: false,
    cleanup: false,
    cleanup_on_startup: false,
  }), []);

  const renderFields = () => {

    const fields = [];

    if (creative === 'Email') {
      fields.push(
        <div className="form-group" key="email-fields">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="input-label">Subject Line 1</label>
              <input
                type="text"
                name="subjectLine1"
                className="form-input"
                value={creativeData[selectedLanguage]?.subjectLine1 || ''}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="input-label">Subject Line 2</label>
              <input
                type="text"
                name="subjectLine2"
                className="form-input"
                value={creativeData[selectedLanguage]?.subjectLine2 || ''}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="input-label">Subject Line 3</label>
              <input
                type="text"
                name="subjectLine3"
                className="form-input"
                value={creativeData[selectedLanguage]?.subjectLine3 || ''}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="input-label">Pre Header</label>
            <input
              type="text"
              name="preHeader"
              className="form-input"
              value={creativeData[selectedLanguage]?.preHeader || ''}
              onChange={handleChange}
            />
          </div>
        </div>
      );
    }

    if (creative === 'Email' || creative === 'Popup') {
      fields.push(
        <div className="form-group" key="main-content">
          <div className="mb-4">
            <label className="input-label">Headline</label>
            <input
              type="text"
              name="headline"
              className="form-input"
              value={creativeData[selectedLanguage]?.headline || ''}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label className="input-label">Body Content</label>
              <PlaceholderDropdown />
            </div>
            <TrumbowygEditor
              key={`body-editor-${selectedLanguage}`}
              value={creativeData[selectedLanguage]?.body || ''}
              onEditorChange={handleEditorChange}
              init={{ height: 500 }}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="input-label">Button Text</label>
              <input
                type="text"
                name="bodyBtnName"
                className="form-input"
                value={creativeData[selectedLanguage]?.bodyBtnName || ''}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="input-label">Button Link</label>
              <input
                type="text"
                name="bodyBtnLink"
                className="form-input"
                value={creativeData[selectedLanguage]?.bodyBtnLink || ''}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
      );
    }

    if (creative === 'Email') {
      fields.push(
        <div className="form-group" key="additional-content">
          <div className="mb-4">
            <label className="input-label">Additional Text</label>
            <TrumbowygEditor
              key={`additional-text-editor-${selectedLanguage}`}
              value={creativeData[selectedLanguage]?.additionalText || ''}
              onEditorChange={handleAdditionalTextChange}
              init={{ height: 200 }}
            />
          </div>
          <div className="mb-4">
            <label className="input-label">Additional Text Under Button</label>
            <TrumbowygEditor
              key={`additional-text-under-btn-editor-${selectedLanguage}`}
              value={creativeData[selectedLanguage]?.additionalTextUnderBTN || ''}
              onEditorChange={handleAdditionalTextUnderBTNChange}
              init={{ height: 200 }}
            />
          </div>
        </div>
      );
    }

    if (creative === 'Sms') {
      const smsBody = creativeData[selectedLanguage]?.body || '';
      const currentStats = calculateSMSStats(smsBody);
      
      fields.push(
        <div className="form-group" key={`sms-content-${selectedLanguage}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label className="input-label">SMS Content</label>
            <PlaceholderDropdown />
          </div>
          <textarea
            name="body"
            className="form-input"
            value={smsBody}
            onChange={handleChange}
            rows={6}
            required
          />
          <div className="sms-stats" key={`stats-${smsBody.length}-${currentStats.nextPart}`}>
            <span>Total Count: {currentStats.totalCount}</span>
            <span>Total SMS: {currentStats.totalSMS}</span>
            <span>GSM Characters: {currentStats.gsm}</span>
            <span>Next Part: {currentStats.nextPart} characters remaining</span>
          </div>
        </div>
      );
    }

    if (creative === 'Push-Notification') {
      fields.push(
        <div className="form-group" key="push-notification-content">
          <div className="mb-4">
            <label className="input-label">Headline</label>
            <input
              type="text"
              name="headline"
              className="form-input"
              value={creativeData[selectedLanguage]?.headline || ''}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label className="input-label">Body Content</label>
              <PlaceholderDropdown />
            </div>
            <textarea
              name="body"
              className="form-input"
              value={creativeData[selectedLanguage]?.body || ''}
              onChange={handleChange}
              rows={6}
              required
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="input-label">Button Text</label>
              <input
                type="text"
                name="bodyBtnName"
                className="form-input"
                value={creativeData[selectedLanguage]?.bodyBtnName || ''}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="input-label">Button Link</label>
              <input
                type="text"
                name="bodyBtnLink"
                className="form-input"
                value={creativeData[selectedLanguage]?.bodyBtnLink || ''}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
      );
    }

    return fields;
  };

  return (
    <div className="edit-creative-container">
      {/* Notification */}
      {notification.show && (
        <motion.div 
          className={`notification ${notification.type}`}
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
        >
          {notification.message}
        </motion.div>
      )}

      {/* Header Section */}
      <div className="header-section">
        <motion.button 
          className="back-button"
          onClick={() => navigate('/library/creatives')}
          whileHover={{ scale: 1.05 }}
        >
          <FiChevronLeft /> Back to Library
        </motion.button>
        <h1 className="page-title">Edit Creative</h1>
        
        <div className="header-actions">
          {/* Version History Button */}
          {canViewHistory && (
            <motion.button
              className="version-history-button"
              onClick={() => setShowVersionHistory(true)}
              whileHover={{ scale: 1.05 }}
              title="View Version History"
            >
              <FiClock /> History
            </motion.button>
          )}
          
          {/* Add Translate Button if there are untranslated languages and user has AI translate permission */}
          {getUntranslatedLanguages().length > 0 && canAITranslateCurrentType && (
            <motion.button
              className="translate-button"
              onClick={() => setShowTranslateModal(true)}
              whileHover={{ scale: 1.05 }}
              title="Translate with AI"
            >
              <FiGlobe /> Translate
            </motion.button>
          )}
          
          {/* Add Language Button - always show if there are untranslated languages */}
          {getUntranslatedLanguages().length > 0 && (
            <motion.button
              className="add-language-button"
              onClick={() => setShowAddLanguageModal(true)}
              whileHover={{ scale: 1.05 }}
              title="Add language manually"
            >
              <FiGlobe /> Add Language
            </motion.button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Language Selector */}
      <div className="language-selector">
        {languages.map((language) => (
          <motion.button
            key={language}
            className={`language-tab ${language === selectedLanguage ? 'active' : ''}`}
            onClick={() => setSelectedLanguage(language)}
            whileHover={{ scale: 1.05 }}
          >
            {getLangCode(language)}
          </motion.button>
        ))}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading creative data...</p>
        </div>
      )}

      {/* Main Form */}
      {languages.length > 0 && content && !isLoading && (
        <motion.form 
          onSubmit={handleSubmit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="creative-form"
        >
          <div className="form-group">
            <label className="input-label">Creative Title</label>
            <input
              type="text"
              name="title"
              className="form-input"
              value={creativeData.title || ''}
              onChange={handleChange}
              required
            />
          </div>

          {/* Tags Input */}
          <div className="form-group">
            <label className="input-label">Tags</label>
            <TagsInput
              value={creativeData.tags || []}
              onChange={(tags) => setCreativeData((prevState) => ({ ...prevState, tags }))}
              placeholder="Type a tag and press Enter"
            />
          </div>

          {/* Campaign Assignment - Show only if user can manage campaigns */}
          {canManageCampaigns && (
            <div className="form-group campaign-assign-section">
              <label className="input-label"><TbCalendarEvent /> Assign to Campaign</label>

              {campaignAssignments.length > 0 && (
                <div className="campaign-assign-list">
                  {campaignAssignments.map((assignment) => (
                    <div key={`${assignment.campaignId}-${assignment.date}`} className="campaign-assign-item">
                      <span className="campaign-assign-item-name">{assignment.campaignName}</span>
                      <span className="campaign-assign-item-date">
                        {format(new Date(assignment.date), 'MMM d, yyyy')}
                      </span>
                      <button
                        type="button"
                        className="campaign-assign-remove-btn"
                        title="Remove from this campaign day"
                        onClick={() => handleRemoveAssignment(assignment.campaignId, assignment.date)}
                      >
                        <TbX />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="campaign-assign-controls">
                <select
                  className="form-select"
                  value={selectedCampaignForAssign}
                  onChange={(e) => {
                    setSelectedCampaignForAssign(e.target.value);
                    setSelectedDateForAssign('');
                  }}
                >
                  <option value="">Select a campaign...</option>
                  {campaigns.map((campaign) => (
                    <option key={campaign._id} value={campaign._id}>{campaign.name}</option>
                  ))}
                </select>

                <select
                  className="form-select"
                  value={selectedDateForAssign}
                  onChange={(e) => setSelectedDateForAssign(e.target.value)}
                  disabled={!selectedCampaignForAssign}
                >
                  <option value="">Select a day...</option>
                  {assignableDays.map((day) => (
                    <option key={toDateKey(day)} value={toDateKey(day)}>
                      {format(day, 'EEE, MMM d, yyyy')}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className="campaign-assign-btn"
                  onClick={handleAssignToCampaign}
                  disabled={!selectedCampaignForAssign || !selectedDateForAssign}
                >
                  <TbPlus /> Assign
                </button>
              </div>

              {selectedCampaignForAssign && assignableDays.length === 0 && (
                <p className="campaign-assign-empty-note">
                  This campaign has no upcoming days available to assign (all its days have already passed).
                </p>
              )}
            </div>
          )}

          {/* Status Selection - Show only if system is enabled */}
          {statusSystemEnabled && (
            <div className="form-group">
              <label className="input-label">Status</label>
              <select
                className="form-select"
                value={currentStatus}
                onChange={(e) => setCurrentStatus(e.target.value)}
              >
                <option value="">Select Status</option>
                {statuses.map((status) => (
                  <option key={status._id} value={status._id}>
                    {status.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {renderFields()}

          <div className="form-actions">
            <motion.button 
              type="submit" 
              className="submit-button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FiSave /> Save
            </motion.button>
          </div>
        </motion.form>
      )}

      {/* Translate Modal */}
      {showTranslateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Translate to Additional Languages</h2>
              <button 
                className="modal-close"
                onClick={() => setShowTranslateModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Select languages to translate this creative to:</p>
              <div className="language-options">
                {getUntranslatedLanguages().map((language) => (
                  <motion.div
                    key={language._id}
                    className={`language-option ${selectedLanguagesToTranslate.includes(language._id) ? 'selected' : ''}`}
                    onClick={() => toggleLanguageForTranslation(language._id)}
                    whileHover={{ scale: 1.02 }}
                  >
                    {language.code?.toUpperCase() || language.languageName}
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <motion.button
                className="cancel-button"
                onClick={() => setShowTranslateModal(false)}
                whileHover={{ scale: 1.03 }}
              >
                Cancel
              </motion.button>
              <motion.button
                className="translate-action-button"
                onClick={handleTranslateToLanguages}
                disabled={isTranslating || selectedLanguagesToTranslate.length === 0}
                whileHover={{ scale: 1.03 }}
              >
                {isTranslating ? (
                  <>
                    <span className="button-spinner"></span>
                    Translating...
                  </>
                ) : (
                  'Translate'
                )}
              </motion.button>
            </div>
          </div>
        </div>
      )}

      {/* Add Language Modal */}
      {showAddLanguageModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add Languages Manually</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowAddLanguageModal(false);
                  setSelectedLanguagesToAdd([]);
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Select languages to add:</p>
              <div className="language-options">
                {getUntranslatedLanguages().map((language) => (
                  <motion.div
                    key={language._id}
                    className={`language-option ${selectedLanguagesToAdd.includes(language._id) ? 'selected' : ''}`}
                    onClick={() => toggleLanguageToAdd(language._id)}
                    whileHover={{ scale: 1.02 }}
                  >
                    {language.code?.toUpperCase() || language.languageName}
                  </motion.div>
                ))}
              </div>
              <p className="modal-note">
                The languages will be added with empty content. You can fill them in manually and save.
              </p>
            </div>
            <div className="modal-footer">
              <motion.button
                className="cancel-button"
                onClick={() => {
                  setShowAddLanguageModal(false);
                  setSelectedLanguagesToAdd([]);
                }}
                whileHover={{ scale: 1.03 }}
              >
                Cancel
              </motion.button>
              <motion.button
                className="translate-action-button"
                onClick={handleAddLanguageManually}
                disabled={selectedLanguagesToAdd.length === 0}
                whileHover={{ scale: 1.03 }}
              >
                <TbPlus /> Add {selectedLanguagesToAdd.length > 0 ? `${selectedLanguagesToAdd.length} ` : ''}Language{selectedLanguagesToAdd.length !== 1 ? 's' : ''}
              </motion.button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Translation Loading Overlay */}
      {isTranslating && (
        <div className="translation-loading-overlay">
          <div className="translation-loading-content">
            <div className="translation-header">
              <h2>Translating Content</h2>
              <p>Please wait while we translate your content to the selected languages</p>
            </div>
            
            <div className="translation-progress">
              <div className="progress-circle">
                <svg className="progress-ring" width="120" height="120">
                  <circle
                    className="progress-ring-circle-bg"
                    stroke="#e5e7eb"
                    strokeWidth="8"
                    fill="transparent"
                    r="52"
                    cx="60"
                    cy="60"
                  />
                  <circle
                    className="progress-ring-circle"
                    stroke="#4a90e2"
                    strokeWidth="8"
                    fill="transparent"
                    r="52"
                    cx="60"
                    cy="60"
                    style={{
                      strokeDasharray: `${2 * Math.PI * 52}`,
                      strokeDashoffset: `${2 * Math.PI * 52 * (1 - translationProgress / 100)}`,
                      transform: 'rotate(-90deg)',
                      transformOrigin: '50% 50%'
                    }}
                  />
                </svg>
                <div className="progress-text">
                  <span className="progress-percentage">{Math.round(translationProgress)}%</span>
                </div>
              </div>
              
              <div className="translation-info">
                <div className="translation-stats">
                  <div className="stat">
                    <span className="stat-label">Languages to translate:</span>
                    <span className="stat-value">{totalLanguages}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Progress:</span>
                    <span className="stat-value">{Math.round(translationProgress)}%</span>
                  </div>
                </div>
                
                {currentTranslatingLanguage && (
                  <div className="current-language">
                    <span className="current-language-label">Currently translating:</span>
                    <span className="current-language-name">{currentTranslatingLanguage}</span>
                  </div>
                )}
                
                <div className="progress-bar">
                  <div className="progress-bar-bg">
                    <div 
                      className="progress-bar-fill"
                      style={{ width: `${translationProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="translation-footer">
              <p className="translation-note">
                This process may take a few moments depending on the amount of content and number of languages.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {showVersionHistory && (
        <VersionHistory 
          creativeId={id}
          creativeType={creative}
          highlightVersionId={highlightVersionId}
          onClose={() => {
            setShowVersionHistory(false);
            setHighlightVersionId(null);
          }}
          onRestore={() => {
            setShowVersionHistory(false);
            setHighlightVersionId(null);
            window.location.reload(); // Reload to show restored version
          }}
        />
      )}

    </div>
  );
};

export default EditCreative;